/**
 * PsychoQuiz — timed, exam-style psychometric flow.
 *
 * Flow:
 *   1. Pre-test card explaining the 20-sec-per-question rule. Founder must
 *      click "I'm ready — start" to begin.
 *   2. One question at a time, full-screen card. A 20-second ring counts
 *      down. If it hits zero before the founder picks an answer, the
 *      question is marked as SKIPPED (= not answered) and the test
 *      auto-advances to the next.
 *   3. On the last question, "Submit report" appears in place of "Next".
 *   4. After submission, redirects to /dashboard/psycho-reports/:reportId
 *      where the founder can see + download their full report.
 *
 * URL: /dashboard/startups/:id/psycho-quiz
 *
 * This is a fresh route (the old StartupDetail "psycho" tab now just
 * deep-links here).
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Compass, Clock, AlertTriangle, CheckCircle2, ChevronRight, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const LIKERT_LABELS = ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'];
const SECONDS_PER_Q = 20;

function CountdownRing({ remaining, total = SECONDS_PER_Q }) {
  const pct = Math.max(0, Math.min(1, remaining / total));
  const warn = remaining <= 5;
  return (
    <div className="relative h-20 w-20" data-testid="psycho-countdown-ring">
      <svg className="absolute inset-0" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r="34" fill="none"
                stroke="#E5E7EB" strokeWidth="6"/>
        <circle cx="40" cy="40" r="34" fill="none"
                stroke={warn ? '#EF4444' : '#8B3FC5'} strokeWidth="6"
                strokeDasharray={`${pct * 213.6} 213.6`}
                strokeLinecap="round" transform="rotate(-90 40 40)"
                style={{ transition: 'stroke-dasharray 1s linear, stroke 0.3s' }}/>
      </svg>
      <div className={`absolute inset-0 flex items-center justify-center font-bold display text-xl ${warn ? 'text-rose-600' : 'text-[#5E1F8B]'}`}>
        {remaining}
      </div>
    </div>
  );
}

/* eslint-disable -- v6 react-hooks/set-state-in-effect + immutability false-positives on quiz/timer effects. */
export default function PsychoQuiz() {
  const { id: startupId } = useParams();
  const nav = useNavigate();
  const [stage, setStage] = useState('preflight'); // preflight | running | submitting
  const [session, setSession] = useState(null);     // { session_id, questions[], seconds_per_question }
  const [idx, setIdx] = useState(0);                // current question index
  const [answers, setAnswers] = useState({});       // qid -> '1'..'5' (missing = skipped)
  const [remaining, setRemaining] = useState(SECONDS_PER_Q);
  const [startedAt, setStartedAt] = useState(null);
  const tickRef = useRef(null);

  const loadSession = async () => {
    try {
      const { data } = await api.get('/startups/psycho/questions');
      setSession(data);
      setIdx(0);
      setAnswers({});
      setRemaining(data.seconds_per_question || SECONDS_PER_Q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load questions.');
    }
  };

  // Stable advance fn (used by both manual Next and timer auto-skip)
  const advance = useCallback(() => {
    if (!session) return;
    setRemaining(session.seconds_per_question || SECONDS_PER_Q);
    if (idx + 1 >= session.questions.length) {
      // End of test — submit
      handleSubmit();
    } else {
      setIdx(idx + 1);
    }
  }, [session, idx, answers, startedAt]);

  // Timer
  useEffect(() => {
    if (stage !== 'running') return;
    tickRef.current && clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          // auto-skip — don't add to answers
          clearInterval(tickRef.current);
          setTimeout(() => advance(), 50); // defer to avoid setState during render
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [stage, idx, advance]);

  const begin = async () => {
    if (!session) await loadSession();
    setStartedAt(new Date().toISOString());
    setStage('running');
  };

  const pickAnswer = (val) => {
    if (!session) return;
    const q = session.questions[idx];
    setAnswers((prev) => ({ ...prev, [q.qid]: String(val) }));
    // Auto-advance 350ms after pick so the user sees the selection
    setTimeout(() => advance(), 350);
  };

  const handleSubmit = async () => {
    setStage('submitting');
    clearInterval(tickRef.current);
    const completedAt = new Date().toISOString();
    const duration = startedAt
      ? Math.round((new Date(completedAt) - new Date(startedAt)) / 1000)
      : null;
    try {
      const { data } = await api.post('/startups/psycho', {
        startup_id: startupId,
        session_id: session?.session_id,
        answers,
        started_at: startedAt,
        duration_seconds: duration,
      });
      toast.success('Report generated.');
      // Redirect to report viewer
      nav(`/dashboard/psycho-reports/${data.report_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit.');
      setStage('running');
    }
  };

  useEffect(() => {
    if (stage === 'preflight' && !session) loadSession();
  }, []);

  // ============= PREFLIGHT =============
  if (stage === 'preflight') {
    return (
      <div className="max-w-2xl mx-auto" data-testid="psycho-preflight">
        <Link to={`/dashboard/startups/${startupId}`} className="ve-btn-ghost text-xs">
          ← Back to startup
        </Link>
        <div className="ve-eyebrow mt-4">Founder Psychometric</div>
        <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
          12 questions. <span className="ve-gold-text">20 seconds each.</span>
        </h1>
        <p className="text-sm text-slate-600 mt-3">
          A short, exam-style self-report. Claude Sonnet uses your answers to produce a Big-5 OCEAN profile, leadership index and risk appetite. Your full report is saved to your founder profile and downloadable as PDF.
        </p>

        <div className="mt-6 ve-card p-5 space-y-3 text-sm">
          <div className="flex items-center gap-3">
            <Clock size={16} className="text-[#5E1F8B] shrink-0"/>
            <span><b>20 seconds per question.</b> If the timer runs out before you pick, the question is marked as <b className="text-rose-700">not answered</b> and the test moves on automatically.</span>
          </div>
          <div className="flex items-center gap-3">
            <Compass size={16} className="text-[#5E1F8B] shrink-0"/>
            <span><b>Questions rotate.</b> Every attempt draws a different set from a balanced OCEAN bank.</span>
          </div>
          <div className="flex items-center gap-3">
            <CheckCircle2 size={16} className="text-[#5E1F8B] shrink-0"/>
            <span><b>One pass is enough.</b> Be honest — there are no &quot;right&quot; answers.</span>
          </div>
          <div className="flex items-center gap-3">
            <AlertTriangle size={16} className="text-rose-600 shrink-0"/>
            <span>Once you click <b>Start</b> the timer begins on question 1. You can&apos;t pause.</span>
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={begin}
            disabled={!session}
            data-testid="psycho-start-btn"
            className="ve-btn-primary"
          >
            {session ? <>I&apos;m ready — start <ChevronRight size={14}/></> : 'Loading…'}
          </button>
          {session && (
            <button onClick={loadSession} className="ve-btn-secondary text-xs">
              <RefreshCw size={12}/> Get a different question set
            </button>
          )}
        </div>
      </div>
    );
  }

  // ============= RUNNING =============
  if (stage === 'running' && session) {
    const q = session.questions[idx];
    const picked = answers[q.qid];
    const isLast = idx === session.questions.length - 1;
    return (
      <div className="max-w-3xl mx-auto" data-testid="psycho-running">
        {/* Top bar — progress + timer */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
              Question {idx + 1} of {session.questions.length}
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-[#8B3FC5] transition-all duration-500"
                style={{ width: `${((idx + 1) / session.questions.length) * 100}%` }}
              />
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-slate-500">
              <span className="text-emerald-700 font-bold">
                {Object.keys(answers).length} answered
              </span>
              {' · '}
              <span className="text-rose-700 font-bold">
                {idx - Object.keys(answers).filter(k => session.questions.slice(0, idx).some(qq => qq.qid === k)).length} skipped
              </span>
            </div>
          </div>
          <CountdownRing remaining={remaining}/>
        </div>

        {/* Question card */}
        <div
          key={q.qid /* re-mount on advance so fade resets */}
          className="ve-card mt-8 p-8 text-center ve-page-enter"
        >
          <div className="text-[9px] uppercase tracking-[0.22em] text-[#5E1F8B] font-bold">
            Dimension · {q.dimension}
          </div>
          <h2 className="display text-2xl md:text-3xl font-bold mt-3 leading-snug">
            {q.text}
          </h2>

          <div className="mt-8 grid grid-cols-5 gap-2 max-w-2xl mx-auto" data-testid="psycho-options">
            {LIKERT_LABELS.map((label, i) => {
              const val = i + 1;
              const selected = picked === String(val);
              return (
                <button
                  key={val}
                  onClick={() => pickAnswer(val)}
                  data-testid={`psycho-pick-${val}`}
                  className={`px-2 py-4 rounded-md border-2 text-[10px] uppercase tracking-[0.12em] font-bold transition-all
                    ${selected
                      ? 'bg-[#8B3FC5] text-[#0A0A0A] border-[#8B3FC5] shadow-md scale-[1.05]'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-[#8B3FC5] hover:text-[#5E1F8B]'}
                  `}
                >
                  <div className="text-2xl font-bold leading-none">{val}</div>
                  <div className="text-[9px] mt-2 opacity-80 leading-tight">{label}</div>
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex items-center justify-between gap-3 text-xs">
            <button
              onClick={() => advance()}
              data-testid="psycho-skip-btn"
              className="text-slate-500 hover:text-slate-800 font-bold uppercase tracking-[0.18em]"
            >
              Skip question
            </button>
            {isLast && (
              <button
                onClick={handleSubmit}
                data-testid="psycho-submit-btn"
                className="ve-btn-primary text-xs"
              >
                Submit report →
              </button>
            )}
          </div>
        </div>

        {remaining <= 5 && (
          <div className="mt-4 text-center text-xs text-rose-700 font-bold uppercase tracking-[0.18em] animate-pulse">
            ⚠ {remaining}s left — answer or this question is marked unanswered.
          </div>
        )}
      </div>
    );
  }

  // ============= SUBMITTING =============
  if (stage === 'submitting') {
    return (
      <div className="ve-card p-10 text-center max-w-md mx-auto" data-testid="psycho-submitting">
        <Compass size={28} className="mx-auto text-[#5E1F8B] animate-pulse"/>
        <h2 className="display text-2xl mt-4">Generating your report…</h2>
        <p className="text-sm text-slate-600 mt-2">Claude Sonnet is producing your Big-5 OCEAN profile.</p>
      </div>
    );
  }
  return null;
}
